import React, {
    useState,
    useEffect,
    useContext,
    useMemo,
} from 'react';

import axios from 'axios';
import _ from 'lodash';
import { convertImageToBase64 } from '../utils';

import sidebarMap from '../constants/sidebarMap';
import useLogout from '../customHooks/useLogout';
import { useAuthentication } from '../customHooks/useAuthentication';
import apiClient from '../apiClient';
import MasterViewChat from '../components/searchAwb/MasterViewChat';

export const AppContext = React.createContext();

export default function Provider(props) {

    const { customLogout } = useLogout();
    const { authUser, isAuthenticated } = useAuthentication();
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Routes
    const [accessMap, setAcessMap] = useState(sidebarMap);
    const [accessMapAssigned, setAccessMapAssigned] = useState(false);

    // User
    const [user, setUser] = useState({});

    // Wikis
    const [wikis, setWikis] = useState([]);
    const [customWikiTitle, setCustomWikiTitle] = useState(null);
    const [accessLevels, setAccessLevels] = useState([]);

    // Newsletters
    const [newsletters, setNewsletters] = useState([]);

    const [trainingAssigned, setTrainingAssigned] = useState([]);

    const [signOut, setSignOut] = useState(false);

    // Reading Signs
    const [readingSigns, setReadingSigns] = useState([]);

    // Select Location/Station/s_unit
    const [modalLocation, setModalLocation] = useState(false);

    // Handle Display Submenu:
    const [displaySubmenu, setDisplaySubmenu] = useState(false);

    const checkForUpdates = async () => {
        try {
            const response = await fetch('/version.json', { cache: 'no-store' });
            const { version: latestVersion } = await response.json();
            const currentVersion = localStorage.getItem('appVersion');

            if (currentVersion !== latestVersion) {
                localStorage.setItem('appVersion', latestVersion);
                window.location.reload(true); // Force reload if version changed
            }
        } catch (error) {
            console.error('Error checking for updates:', error);
        }
    };

    const requestAbstractApiKey = async () => {
        const res = await apiClient.get(`/abstract-api-key`);
        return res.data;
    }

    async function getIpGeolocation() {
        try {
            const key = await requestAbstractApiKey();

            // Try primary API first
            const ipRes = await axios.get(
                `https://ipgeolocation.abstractapi.com/v1/?api_key=${key}`
            );

            console.log(ipRes);

            if (ipRes.status === 200 && ipRes.data) {
                const { ip_address, longitude, latitude, city, is_vpn } = ipRes.data;
                return {
                    ipAddress: ip_address,
                    city: city || 'unknown',
                    longitude: longitude || null,
                    latitude: latitude || null,
                    is_vpn: is_vpn || false,
                    success: true
                };
            } else {
                throw new Error('Primary API returned invalid response');
            }
        } catch (err) {
            console.warn('Primary IP API failed, using fallback:', err.message);

            try {
                // Fallback to secondary API
                const ipRes = await axios.get('https://ipapi.co/json/');

                if (ipRes.status === 200 && ipRes.data) {
                    const { ip, latitude, longitude, city } = ipRes.data;
                    return {
                        ipAddress: ip,
                        city: city || 'unknown',
                        longitude: longitude || null,
                        latitude: latitude || null,
                        is_vpn: false, // Not available from fallback API
                        success: true
                    };
                } else {
                    throw new Error('Fallback API returned invalid response');
                }
            } catch (fallbackErr) {
                console.error('Both IP APIs failed:', fallbackErr.message);

                // Return default values when both APIs fail
                return {
                    ipAddress: null,
                    city: 'unknown',
                    longitude: null,
                    latitude: null,
                    is_vpn: false,
                    success: false
                };
            }
        }
    }

    useEffect(() => {
        const resolveAccessMap = (userAccessMap) => {
            // Create a copy to avoid mutating the original
            const resolvedMap = _.cloneDeep(userAccessMap);

            for (let tabId in resolvedMap) {
                const userTab = resolvedMap[tabId];
                const sidebarTab = sidebarMap?.[tabId];

                // If tab doesn't exist in sidebar, remove it entirely
                if (!sidebarTab) {
                    delete resolvedMap[tabId];
                    continue;
                }

                // Add component and icon if they exist
                if (sidebarTab.component) {
                    userTab.component = sidebarTab.component;
                    userTab.icon = sidebarTab.icon;
                }

                const subs = userTab.subs;
                const sidebarSubs = sidebarTab?.subs;

                if (!subs || !sidebarSubs) continue;

                for (let subId in subs) {
                    const userSub = subs[subId];
                    const sidebarSub = sidebarSubs?.[subId];

                    // If sub doesn't exist in sidebar, remove it
                    if (!sidebarSub) {
                        delete subs[subId];
                        continue;
                    }

                    // Add component and icon if they exist
                    if (sidebarSub.component) {
                        userSub.component = sidebarSub.component;
                        userSub.icon = sidebarSub.icon;
                    }

                    const finalSubs = userSub.subs;
                    const sidebarFinalSubs = sidebarSub?.subs;

                    if (!finalSubs || !sidebarFinalSubs) continue;

                    for (let finalSubId in finalSubs) {
                        const userFinal = finalSubs[finalSubId];
                        const sidebarFinal = sidebarFinalSubs?.[finalSubId];

                        // If final sub doesn't exist in sidebar, remove it
                        if (!sidebarFinal) {
                            delete finalSubs[finalSubId];
                            continue;
                        }

                        // Add component and icon if they exist
                        if (sidebarFinal.component) {
                            userFinal.component = sidebarFinal.component;
                            userFinal.icon = sidebarFinal.icon;
                        }
                    }
                }
            }

            return resolvedMap;
        };

        const checkEnvironment = (environmentUrl) => {
            if (!environmentUrl) return;
            setIsRedirecting(true);
            const currentDomain = window.location.origin;
            if (currentDomain !== environmentUrl) {
                window.location.href = environmentUrl;
            }
            setIsRedirecting(false);
        }

        const getUserData = async () => {
            const s_email = authUser.email;
            const { testRegUser } = props;
            if (s_email) {
                const { ipAddress, city, longitude, latitude, is_vpn } = await getIpGeolocation();;

                if (!ipAddress) {
                    alert('Error getting IP Address');
                    await customLogout();
                }

                const res = await apiClient.post('/userData', {
                    s_email,
                    testRegUser,
                    ipAddress,
                    city,
                    longitude,
                    latitude,
                    is_vpn
                });

                if (!res || res.status !== 200) {
                    await customLogout();
                }

                const {
                    user,
                    readingSigns,
                    accessMap,
                    newsletters,
                    trainingAssigned,
                    environmentUrl
                } = res.data;

                checkEnvironment(environmentUrl);

                if (user.s_unit === 'HDQ') {
                    setModalLocation(true);
                }

                user.connectedToBackend = true;

                if (!user.photo) {
                    user.photo = convertImageToBase64(authUser.picture);
                }

                if (!user.localAccountId) {
                    user.localAccountId = authUser.sub;
                }

                // anyone give access to managers/sharedPasswords in access mapping has is granted sharedVaultAccess
                const sharedVaultAccess = _.get(accessMap, ['managers', 'subs', 'sharedPasswords'], null) !== null;
                user.sharedVaultAccess = sharedVaultAccess;

                setUser(user);
                setReadingSigns(readingSigns);

                setAcessMap(resolveAccessMap(accessMap));
                setAccessMapAssigned(true);
                setNewsletters(newsletters);
                setTrainingAssigned(trainingAssigned);

                checkForUpdates();
            }
        };

        if (isAuthenticated) {
            getUserData();
        }
    }, [isAuthenticated]);

    // Modal Loading
    const [loading, setLoading] = useState(false);

    const memoizedValue = useMemo(() => {
        return {
            wiki: {
                wikis,
                setWikis,
                accessLevels,
                setAccessLevels,
                customWikiTitle,
                setCustomWikiTitle,
            },
            isRedirecting,
            user,
            setUser,
            accessMap,
            accessMapAssigned,
            readingSigns,
            setReadingSigns,
            modalLocation,
            setModalLocation,
            displaySubmenu,
            setDisplaySubmenu,
            loading,
            setLoading,
            signOut,
            setSignOut,
            newsletters,
            trainingAssigned
        };
    }, [accessLevels, accessMap, accessMapAssigned, customWikiTitle, displaySubmenu, loading, modalLocation, readingSigns, signOut, user, wikis]);

    return (
        <AppContext.Provider value={memoizedValue}>
            {props.children}
            <MasterViewChat />
        </AppContext.Provider>
    );
}

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('AppContext is missing');
    }
    return context;
};
